// Session expiry warning banner for partner portal.
// Shows a countdown 2 minutes before token expires.
// "Extend session" button triggers a manual token refresh.
// Cleans up interval on unmount (no memory leak).

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useLang, fmt } from '../i18n';
import { colors } from './ui/styles';

const WARNING_THRESHOLD_SEC = 120; // show warning 2 min before expiry

export function SessionWarning() {
  const auth = useAuth();
  const { t } = useLang();
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const [extending, setExtending] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!auth.authenticated || auth.expiresAt === 0) {
      setSecondsLeft(null);
      return;
    }

    const tick = () => {
      const now = Math.floor(Date.now() / 1000);
      const remaining = auth.expiresAt - now;
      if (remaining <= WARNING_THRESHOLD_SEC && remaining > 0) {
        setSecondsLeft(remaining);
      } else if (remaining <= 0) {
        setSecondsLeft(0);
      } else {
        setSecondsLeft(null);
      }
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [auth.authenticated, auth.expiresAt]);

  const handleExtend = useCallback(async () => {
    setExtending(true);
    try {
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'same-origin',
      });
      if (res.ok) {
        setDismissed(true);
        setSecondsLeft(null);
        // The useAuth hook will re-read the signal cookie on next cycle
        // and update expiresAt, so the warning will naturally disappear.
      }
    } catch { /* silent */ }
    setExtending(false);
  }, []);

  // Don't show if: not in warning zone, dismissed, or session already expired and user redirected
  if (secondsLeft === null || dismissed) return null;

  // Session fully expired
  if (secondsLeft <= 0) {
    return (
      <div style={{
        padding: '10px 16px',
        background: colors.dangerBg,
        borderBottom: `1px solid ${colors.dangerBorder}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        fontSize: '0.85rem',
        color: colors.dangerAlt,
      }}>
        {t.session.expired}
      </div>
    );
  }

  return (
    <div style={{
      padding: '8px 16px',
      background: colors.warningBg,
      borderBottom: `1px solid ${colors.warningBorder}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 12,
      fontSize: '0.85rem',
      color: colors.warningAlt,
      flexWrap: 'wrap',
    }}>
      <span>{fmt(t.session.expiringMsg, { seconds: secondsLeft })}</span>
      <button
        onClick={handleExtend}
        disabled={extending}
        style={{
          padding: '4px 12px',
          borderRadius: 6,
          border: `1px solid ${colors.warningBorder}`,
          background: 'rgba(251, 191, 36, 0.15)',
          color: colors.warningAlt,
          fontSize: '0.8rem',
          cursor: 'pointer',
          fontWeight: 600,
        }}
      >
        {extending ? '...' : t.session.extend}
      </button>
    </div>
  );
}
